import React, { useEffect, useState } from 'react';

import {
  Breakpoints,
  CTA,
  Infobox,
  InputAppearanceValue,
  LoaderSpinner,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
  TextInput,
  ThemeProvider,
} from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  setResetPinModuleToSessionStorage,
  setSecurityPinCompletionRedirectUrlToSessionStorage,
} from '@vw-marketing/myvw-portal-web-utils/Utils/Helpers/Helper';

import { applyTextTransformations } from '../../../../shared/utils/applyTextTransformations';
import {
  InfoboxContainer,
  StyledButtonsContainer,
  StyledFormWrapper,
  StyledInputsContainer,
  StyledVerificationCodeWrapper,
} from './styles';
import {
  onVWBasicStepLoadEvent,
  onVWBasicButtonClickWithProcessEvent,
  onVWBasicLayerLoadEvent,
} from '../../../../shared/tagging/tagging-helpers';

interface EditSecurityPinViewProps {
  authService: any;
  translationHandler: (key: string) => string;
  trackingManager: any;
  localeCode: string;
  onTitleChange: (title: string, noTranslateTerms: string[]) => void;
  onSuccess: () => void;
  onChange: () => void;
  isCanada: boolean;
}

export const EditSecurityPinView: React.FC<EditSecurityPinViewProps> = ({
  authService,
  translationHandler,
  trackingManager,
  localeCode,
  onTitleChange,
  onChange,
  onSuccess,
  isCanada
}) => {
  const [step, setStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (step === 0) {
      onTitleChange(translationHandler('accountSettings.edit.pin.verification.title'), []);

      try {
        onVWBasicStepLoadEvent(trackingManager, {
          Name: 'Account Settings',
          StepName: 'Personal Information and Security - Verification Code',
          StepNumber: '4.1',
        });

        if (isCanada) {
          onVWBasicLayerLoadEvent(trackingManager, 'Security - Get Verification Code');
        }
      } catch (e) {
        console.warn('Error tracking verification code step load:', e);
      }
    } else {
      onTitleChange(translationHandler('accountSettings.edit.pin.description'), ['PIN']);
      try {
        onVWBasicStepLoadEvent(trackingManager, {
          Name: 'Account Settings',
          StepName: 'Personal Information and Security - Update PIN',
          StepNumber: '4.2',
        });
      } catch (e) {
        console.warn('Error tracking PIN update step load:', e);
      }
    }
  }, [step, trackingManager]);

  const validationSchema = Yup.object({
    otp: Yup.string()
      .required(translationHandler('accountSettings.edit.pin.update.code.error.empty'))
      .length(6, translationHandler('accountSettings.edit.pin.update.code.error'))
      .matches(/^\d+$/, translationHandler('accountSettings.edit.pin.update.code.error')),
    securityPin: Yup.string()
      .required(translationHandler('accountSettings.edit.pin.update.pin.error.empty'))
      .length(4, translationHandler('accountSettings.edit.pin.update.pin.error'))
      .matches(/^\d+$/, translationHandler('accountSettings.edit.pin.update.pin.error')),
  });

  const handleSendSMS = async () => {
    try {
      onVWBasicButtonClickWithProcessEvent(trackingManager, {
        Name: 'Account Settings',
        StepName: 'Personal Information and Security - Verification Code',
        StepNumber: '4.1',
      }, { name: 'Send SMS' });
    } catch (e) {
      console.warn('Error tracking Send SMS button click:', e);
    }
    try {
      setError(null);
      setIsLoading(true);
      setResetPinModuleToSessionStorage();
      setSecurityPinCompletionRedirectUrlToSessionStorage(window.location.pathname);
      await authService.getOTPGqlRequest(localeCode);
      setStep(1);
    } catch (e) {
      console.error(e);
      setError('accountSettings.edit.pin.verification.error');
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: { otp: '', securityPin: '' },
    validationSchema,
    onSubmit: async (values) => {
      try {
        onVWBasicButtonClickWithProcessEvent(trackingManager, {
          Name: 'Account Settings',
          StepName: 'Personal Information and Security - Update PIN',
          StepNumber: '4.2',
        }, { name: 'Update PIN' });
      } catch (e) {
        console.warn('Error tracking Update PIN button click:', e);
      }
      try {
        setIsLoading(true);
        setError(null);
        await authService.saveResetPin({ pin: values.securityPin, otp: values.otp }, localeCode);
        onSuccess();
      } catch (e) {
        console.error(e);
        setError('accountSettings.edit.pin.update.error');
      } finally {
        setIsLoading(false);
      }
    },
  });

  const getFieldProps = (name: string) => ({
    name,
    onChange: (e: React.SyntheticEvent<HTMLInputElement>) => { formik.handleChange(e); },
    onBlur: formik.handleBlur,
    value: formik.values[name],
    message: formik.touched[name] && formik.errors[name] ? formik.errors[name] : undefined,
    appearance: formik.touched[name] && formik.errors[name] ? ('error' as InputAppearanceValue) : undefined,
  });

  const verifyNumericInput = (e: React.SyntheticEvent<HTMLInputElement, Event>, inputName: 'otp' | 'securityPin') => {
    const value = (e.target as HTMLInputElement).value.replace(/\D/g, '');
    formik.setFieldValue(inputName, value);
  };

  useEffect(() => {
    if (formik.dirty && onChange) onChange();
  }, [formik.values, onChange]);

  const isButtonDisabled = Object.keys(formik.errors).length > 0 || !formik.dirty || formik.isSubmitting || isLoading;

  if (isLoading) return <LoaderSpinner />;

  if (step === 0) {
    return (
      <ThemeProvider theme="main">
        <StyledVerificationCodeWrapper>
          {error ? (
            <InfoboxContainer className="info-box-error">
              <Infobox>{<>{applyTextTransformations(translationHandler(error), ['PIN'])}</>}</Infobox>
            </InfoboxContainer>
          ) : (
            <>
              <TextCustom
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize16,
                    color: TextColor.primary,
                    fontWeight: TextWeight.light,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {applyTextTransformations(translationHandler('accountSettings.edit.pin.verification.description'), ['PIN'])}
              </TextCustom>
              <CTA
                stretchContent
                aria-label="Save button"
                disabled={isLoading}
                emphasis="primary"
                tag="button"
                type="submit"
                onClick={handleSendSMS}
              >
                {translationHandler('accountSettings.edit.pin.verification.button')}
              </CTA>
            </>
          )}
        </StyledVerificationCodeWrapper>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme="main">
      <StyledFormWrapper className="security-pin-form">
        {error ? (
          <InfoboxContainer className="info-box-error">
            <Infobox>{translationHandler(error)}</Infobox>
          </InfoboxContainer>
        ) : (
          <>
            <TextCustom
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize16,
                  color: TextColor.primary,
                  fontWeight: TextWeight.light,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
              }}
            >
              {applyTextTransformations(translationHandler('accountSettings.edit.pin.update.description'), ['PIN'])}
            </TextCustom>
            <form onSubmit={formik.handleSubmit}>
              <StyledInputsContainer className="inputs-container">
                <TextInput
                  {...getFieldProps('otp')}
                  onChange={(e: React.SyntheticEvent<HTMLInputElement, Event>) => { verifyNumericInput(e, 'otp'); }}
                  aria-label={translationHandler('accountSettings.edit.pin.update.code.label')}
                  aria-required="true"
                  required
                  label={translationHandler('accountSettings.edit.pin.update.code.label')}
                  inputMode="numeric"
                  data-testid="input-otp"
                  maxLength={6}
                />
                <TextInput
                  {...getFieldProps('securityPin')}
                  aria-label={translationHandler('accountSettings.edit.pin.update.pin.label')}
                  onChange={(e: React.SyntheticEvent<HTMLInputElement, Event>) => { verifyNumericInput(e, 'securityPin'); }}
                  aria-required="true"
                  required
                  label={applyTextTransformations(translationHandler('accountSettings.edit.pin.update.pin.label'), ['PIN']) as any}
                  inputMode="numeric"
                  data-testid="input-securityPin"
                  maxLength={4}
                />
              </StyledInputsContainer>
              <StyledButtonsContainer>
                <CTA stretchContent aria-label="Save button" disabled={isButtonDisabled} emphasis="primary" tag="button" type="submit">
                  {applyTextTransformations(translationHandler('accountSettings.edit.pin.update.button'), ['PIN'])}
                </CTA>
              </StyledButtonsContainer>
            </form>
          </>
        )}
      </StyledFormWrapper>
    </ThemeProvider>
  );
};
